// Iter 82.3 / Phase E1.3 — Pending Approval Report.
// Aged backlog of unresolved leave requests with breakdown by stage role
// and CSV/XLSX exports.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FunnelSimple, FileXls, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

export default function LeavePendingApprovalReport() {
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [policies, setPolicies] = useState([]);
    const [filters, setFilters] = useState({ department: "", leave_type_code: "" });
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(true);

    const loadMeta = useCallback(async () => {
        try {
            const [hr, pl] = await Promise.all([
                api.get("/hr/employee/meta"),
                api.get("/leave/policy/list"),
            ]);
            setHrMeta(hr.data);
            setPolicies(pl.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load meta"));
        }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/leave/report/pending-approval", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load report"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/leave/report/pending-approval/export.${ext}`, {
                params, responseType: "blob",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-pending-approval-${new Date().toISOString().slice(0, 10)}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    const s = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-pending-page">
            <PageHeader
                eyebrow="Leave Reports"
                title="Pending Approval Report"
                description="Aged backlog of leave requests awaiting decision, grouped by stage role."
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="pa-export-csv">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="pa-export-xlsx">
                            <FileXls size={14} className="mr-1" /> Excel
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pa-summary">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Total Pending</div>
                    <div className="text-3xl font-bold text-amber-900 mt-1">{s.total_pending || 0}</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-orange-800 font-semibold">Pending &gt; 3 Days</div>
                    <div className="text-3xl font-bold text-orange-900 mt-1">{s.pending_gt_3_days || 0}</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-rose-800 font-semibold">Pending &gt; 7 Days</div>
                    <div className="text-3xl font-bold text-rose-900 mt-1">{s.pending_gt_7_days || 0}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Longest Pending</div>
                    <div className="text-3xl font-bold text-slate-900 mt-1">{(s.longest_pending_days || 0).toFixed(1)}<span className="text-sm font-normal text-slate-500">d</span></div>
                </div>
            </section>

            {Object.keys(s.by_role || {}).length > 0 && (
                <section className="mt-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="pa-by-role">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">By Current Stage Role</h4>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(s.by_role).map(([role, count]) => (
                            <Badge key={role} variant="outline" className="text-xs" data-testid={`pa-role-${role}`}>
                                {role.replace(/_/g, " ")}: <strong className="ml-1">{count}</strong>
                            </Badge>
                        ))}
                    </div>
                </section>
            )}

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="pa-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select data-testid="pa-filter-dept" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (<option key={d.id || d.name} value={d.name}>{d.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select data-testid="pa-filter-type" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.leave_type_code} onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value })}>
                            <option value="">All</option>
                            {policies.map((p) => (<option key={p.code} value={p.code}>{p.code}</option>))}
                        </select>
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="pa-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Request</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Period</th>
                                <th className="text-right px-3 py-2 font-medium">Days</th>
                                <th className="text-left px-3 py-2 font-medium">Current Stage</th>
                                <th className="text-right px-3 py-2 font-medium">Age (days)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-400">Loading…</td></tr>}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No pending requests.</td></tr>
                            )}
                            {!loading && data.items.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pa-row-${r.id}`}>
                                    <td className="px-3 py-2"><Link to={`/leave/requests/${r.id}`} className="font-mono text-xs text-sky-700 hover:underline">{r.id}</Link></td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-900">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code} · {r.department || "—"}</div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                    <td className="px-3 py-2 text-xs">{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{Number(r.days_total).toFixed(1)}</td>
                                    <td className="px-3 py-2 text-xs">{r.current_stage_label || r.current_stage_role}</td>
                                    <td className="px-3 py-2 text-right">
                                        {r.age_days > 7 ? <Badge className="bg-rose-100 text-rose-800 text-xs"><Warning size={10} className="inline mr-0.5" />{r.age_days.toFixed(1)}d</Badge> :
                                         r.age_days > 3 ? <Badge className="bg-orange-100 text-orange-800 text-xs">{r.age_days.toFixed(1)}d</Badge> :
                                         <Badge variant="outline" className="text-xs">{r.age_days.toFixed(1)}d</Badge>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
